import React, { useState } from 'react';
import AppHeader from '../../../components/AppHeader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStickyNote, faPlus, faEdit, faTrash, faUser, faSearch, faFilter, faSave, faTimes, faEye, faTag, faCalendar } from '@fortawesome/free-solid-svg-icons';

const CustomerNotesPage: React.FC = () => {
  const [selectedCustomer, setSelectedCustomer] = useState('1');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNote, setNewNote] = useState({ title: '', content: '', tags: '', priority: 'normal' });

  const breadcrumb = [{ label: 'Zákazníci' }, { label: 'Poznámky o zákazníkovi' }];

  const customers = [
    { id: '1', name: 'Jana Nováková', email: 'jana.novakova@email.cz', phone: '+420 123 456 789' },
    { id: '2', name: 'Petr Svoboda', email: 'petr.svoboda@email.cz', phone: '+420 987 654 321' },
    { id: '3', name: 'Marie Krásná', email: 'marie.krasna@email.cz', phone: '+420 555 666 777' },
  ];

  const notes = [
    {
      id: '1',
      customerId: '1',
      title: 'Preference vlasů',
      content: 'Preferuje kratší střih, nechce barvení. Má citlivou pokožku hlavy.',
      tags: ['vlasy', 'preference', 'alergie'],
      priority: 'high',
      author: 'Anna Svobodová',
      createdAt: '2024-11-15',
      updatedAt: '2024-11-15',
    },
    {
      id: '2',
      customerId: '1',
      title: 'Spokojenost se službami',
      content: 'Velmi spokojená klientka, pravidelně chodí každé 3 měsíce. Vždy objednává střih a foukanou.',
      tags: ['spokojenost', 'pravidelnost'],
      priority: 'normal',
      author: 'Marie Krásná',
      createdAt: '2024-10-20',
      updatedAt: '2024-10-20',
    },
    {
      id: '3',
      customerId: '1',
      title: 'Kontaktní informace',
      content: 'Preferuje komunikaci přes SMS. Ráno je nejlepší čas pro kontaktování.',
      tags: ['komunikace', 'kontakt'],
      priority: 'normal',
      author: 'Jana Nová',
      createdAt: '2024-09-15',
      updatedAt: '2024-09-15',
    },
    {
      id: '4',
      customerId: '2',
      title: 'Alergie na produkty',
      content: 'Má alergii na amoniak, používat pouze bezamoniakové barvy.',
      tags: ['alergie', 'barvení', 'zdraví'],
      priority: 'high',
      author: 'Petr Novák',
      createdAt: '2024-11-10',
      updatedAt: '2024-11-10',
    },
    {
      id: '5',
      customerId: '2',
      title: 'Preference termínů',
      content: 'Může chodit pouze odpoledne po 16:00 kvůli práci.',
      tags: ['termíny', 'pracovní doba'],
      priority: 'normal',
      author: 'Tomáš Vlasák',
      createdAt: '2024-10-05',
      updatedAt: '2024-10-05',
    },
  ];

  const filteredNotes = notes.filter(note => {
    const matchesCustomer = note.customerId === selectedCustomer;
    const matchesSearch =
      note.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCustomer && matchesSearch;
  });

  const selectedCustomerData = customers.find(c => c.id === selectedCustomer);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'normal':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityText = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'Vysoká';
      case 'medium':
        return 'Střední';
      case 'normal':
        return 'Normální';
      default:
        return 'Neznámá';
    }
  };

  const handleAddNote = () => {
    if (newNote.title && newNote.content) {
      // Handle adding new note
      console.log('Adding note:', newNote);
      setNewNote({ title: '', content: '', tags: '', priority: 'normal' });
      setShowAddNote(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Poznámky o zákazníkovi" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {/* Customer Selection */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Výběr zákazníka</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <select
                value={selectedCustomer}
                onChange={e => setSelectedCustomer(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
              >
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>
          </section>

          {/* Customer Info */}
          {selectedCustomerData && (
            <section className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <FontAwesomeIcon icon={faUser} className="w-8 h-8 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{selectedCustomerData.name}</h3>
                    <p className="text-gray-600">{selectedCustomerData.email}</p>
                    <p className="text-gray-600">{selectedCustomerData.phone}</p>
                  </div>
                </div>
                <button onClick={() => setShowAddNote(true)} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  Nová poznámka
                </button>
              </div>
            </section>
          )}

          {/* Add Note Modal */}
          {showAddNote && (
            <section className="bg-white rounded-lg shadow-sm p-6 border-2 border-green-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Nová poznámka</h3>
                <button onClick={() => setShowAddNote(false)} className="text-gray-400 hover:text-gray-600">
                  <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Název poznámky</label>
                  <input
                    type="text"
                    value={newNote.title}
                    onChange={e => setNewNote({ ...newNote, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                    placeholder="Zadejte název poznámky"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Obsah poznámky</label>
                  <textarea
                    rows={4}
                    value={newNote.content}
                    onChange={e => setNewNote({ ...newNote, content: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                    placeholder="Zadejte obsah poznámky"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Štítky (oddělené čárkou)</label>
                    <input
                      type="text"
                      value={newNote.tags}
                      onChange={e => setNewNote({ ...newNote, tags: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                      placeholder="např. preference, alergie, kontakt"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Priorita</label>
                    <select
                      value={newNote.priority}
                      onChange={e => setNewNote({ ...newNote, priority: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                    >
                      <option value="normal">Normální</option>
                      <option value="medium">Střední</option>
                      <option value="high">Vysoká</option>
                    </select>
                  </div>
                </div>
                <div className="flex items-center justify-end space-x-3">
                  <button onClick={() => setShowAddNote(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
                    Zrušit
                  </button>
                  <button onClick={handleAddNote} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                    Uložit poznámku
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* Search */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Hledat v poznámkách..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                />
              </div>
              <button className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                <FontAwesomeIcon icon={faFilter} className="w-4 h-4 mr-2" />
                Filtry
              </button>
            </div>
          </section>

          {/* Notes List */}
          <section className="space-y-4">
            {filteredNotes.map(note => (
              <div key={note.id} className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-medium text-gray-900">{note.title}</h3>
                      <span className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(note.priority)}`}>{getPriorityText(note.priority)}</span>
                    </div>
                    <p className="text-gray-600 mb-3">{note.content}</p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {note.tags.map((tag, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                          <FontAwesomeIcon icon={faTag} className="w-3 h-3 mr-1" />
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <FontAwesomeIcon icon={faUser} className="w-4 h-4 mr-2" />
                      {note.author} •
                      <FontAwesomeIcon icon={faCalendar} className="w-4 h-4 mr-1 ml-2" />
                      {new Date(note.createdAt).toLocaleDateString('cs-CZ')}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button className="text-blue-600 hover:text-blue-900 p-1" title="Zobrazit">
                      <FontAwesomeIcon icon={faEye} className="w-4 h-4" />
                    </button>
                    <button className="text-gray-600 hover:text-gray-900 p-1" title="Upravit">
                      <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                    </button>
                    <button className="text-red-600 hover:text-red-900 p-1" title="Smazat">
                      <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </section>

          {filteredNotes.length === 0 && (
            <section className="bg-white rounded-lg shadow-sm p-12 text-center">
              <FontAwesomeIcon icon={faStickyNote} className="w-16 h-16 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Žádné poznámky</h3>
              <p className="text-gray-600 mb-4">Pro tohoto zákazníka zatím nejsou žádné poznámky.</p>
              <button onClick={() => setShowAddNote(true)} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Přidat první poznámku
              </button>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerNotesPage;
